// frontend/src/config/moduleTheme.js
//
// Per-module accent colour, resolved automatically from the current route.
// SAP Fiori "Spaces" idea: each line-of-business wears its own colour. We reuse
// the RBAC catalog (moduleCatalog.ROLE_EDITOR_GROUPS) as the single source of
// truth for path → group, so there is no second list to keep in sync.
//
// ModuleThemeProvider writes the resolved accent to CSS variables on a wrapper
// div; the design-system kit reads `var(--aone-accent, …)` (see components.js).
// The dashboard route ('/') and any unmatched path resolve to the green default,
// so the founder dashboard and unknown pages are visually unchanged.

import React, { useMemo } from 'react';
import { useLocation } from 'react-router';
import { ROLE_EDITOR_GROUPS } from './moduleCatalog';
import { T } from '../design-system/tokens';

// Group (from moduleCatalog) → T.module key. Exported so the sidebar can look
// up the same muted per-group palette for its small icon-tile tint, even
// though page-content accent itself is collapsed to one wine/bronze system
// (see resolveModuleAccent below).
export const GROUP_TO_MODULE = {
  'Core / Workspace':                'sales',        // green default
  'Master Data':                     'rd',
  'Sales & Distribution':            'sales',   // legacy name, kept for safety
  'Sales & Orders':                  'sales',
  'CRM & Customer Service':          'customerCare',
  'E-Commerce & Channels':           'sales',
  'B2B / Trade Portal':              'sales',
  'Marketing & Creative':            'packaging',
  'R&D / PLM':                       'rd',
  'Artwork & Labels':                'packaging',
  'Regulatory & EHS':                'finishedGoods',
  'Quality Management':              'finishedGoods',
  'Manufacturing':                   'production',
  'Sourcing & Procurement':          'procurement',
  'Warehouse & Inventory':           'inventory',
  'Logistics & Dispatch':            'inventory',
  'Asset & Maintenance':             'production',
  'Finance & Controlling':           'finance',
  'Human Resources':                 'hr',
  'Analytics & Intelligence':        'teal',
  'Investor Readiness':              'slate',
  'Reseller Portal':                 'sales',
  'Governance, Risk & Compliance':   'garnet',
  'System Administration':           'hr',
  // Nav-only group names (navCatalog.js) — aliases so sidebar tile tints
  // don't fall through to the green default for groups that exist only in
  // the nav taxonomy.
  'Salesforce':                      'sales',
  'OMS / Order Control':             'sales',
  'Freepik':                         'packaging',
  'Social & Ads':                    'packaging',
  'Email & Automation':              'packaging',
  'Brand & Assets':                  'packaging',
  'Promotion':                       'packaging',
  'Quality':                         'finishedGoods',
  'Production':                      'production',
  'Supply Chain':                    'procurement',
  'Dispatch & Logistics':            'inventory',
  'Finance':                         'finance',
  'Reports & Analytics':             'teal',
  'Vendor Portal':                   'procurement',
};

const GREEN = T.module.sales;

// path → module palette, built once. Longest-prefix match handles nested
// routes (e.g. /buyer-pos/123 → /buyer-pos).
const PATH_TO_MODULE = (() => {
  const map = [];
  for (const g of ROLE_EDITOR_GROUPS) {
    const key = GROUP_TO_MODULE[g.group] || 'sales';
    const palette = T.module[key] || GREEN;
    for (const m of g.modules) {
      if (m.path && m.path !== '/') map.push([m.path, palette]);
    }
  }
  // longest paths first so the most specific prefix wins
  return map.sort((a, b) => b[0].length - a[0].length);
})();

// The per-module rainbow is collapsed to one wine/bronze accent everywhere —
// same call already made for the sidebar (see Layout.js NAV_GROUP_GRADIENTS/
// NAV_GROUP_PASTEL). PATH_TO_MODULE above is left intact rather than removed
// so per-module theming can be restored by reverting this one line.
export function resolveModuleAccent(pathname = '/') {
  return GREEN;
}

// Wraps page content; sets the accent CSS variables for the current route.
export function ModuleThemeProvider({ children }) {
  const { pathname } = useLocation();
  const palette = useMemo(() => resolveModuleAccent(pathname), [pathname]);
  const style = {
    '--aone-accent': palette.accent,
    '--aone-accent-hover': palette.hover || palette.accent,
    '--aone-accent-soft': palette.soft,
    '--aone-accent-mid': palette.mid,
    '--aone-hero': palette.hero,
    '--aone-content-wash': `linear-gradient(180deg, ${palette.soft} 0%, rgba(247,249,247,0) 34%)`,
    display: 'contents',   // no layout box — purely a CSS-variable scope
  };
  return <div style={style}>{children}</div>;
}

export default ModuleThemeProvider;
